import Tooltip from "@mui/material/Tooltip";
import React from "react";
import colors from "styles/Colors";
import { tokens } from "styles/Tokens";

interface ProgressData {
  completed: number;
  overdue: number;
  active?: number; // optional
  total: number;
}

interface CircularProgressBarProps {
  size?: number; // diameter
  strokeWidth?: number;
  strokeColor?: string;
  variant?: string;
  data: ProgressData;
  tooltipCompleted?: React.ReactNode;
  tooltipOverdue?: React.ReactNode;
  tooltipActive?: React.ReactNode;
  tooltipCenter?: React.ReactNode;
}

const COLORS = {
  completed: tokens.Text.Feedback.Success,
  overdue: tokens.Text.Feedback.OverdueLight,
  active: colors.Neutral.Neutral0,
  base: colors.Neutral.Neutral200
};

const CircularProgressBar: React.FC<CircularProgressBarProps> = ({
  variant,
  size = 50,
  strokeWidth = 12,
  strokeColor = COLORS.base,
  data,
  tooltipCompleted,
  tooltipOverdue,
  tooltipActive,
  tooltipCenter
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const center = size / 2;

  const { completed, overdue, active = 0, total } = data;

  const redLength = (overdue / total) * circumference;
  const greenLength = (completed / total) * circumference;
  const whiteLength = (active / total) * circumference;

  const redOffset = circumference; // red starts at 0
  const greenOffset = circumference - redLength; // green starts after red
  const whiteOffset = circumference - redLength - greenLength; // white starts after green

  // A transparent inner circle radius for hover (inside the progress ring)
  const innerRadius = radius - strokeWidth / 2;

  return (
    <svg
      width={size}
      height={size}
      style={{ transform: "rotate(-90deg)" }}
      role="img"
      aria-label="Circular progress bar"
    >
      {/* Base track */}
      <Tooltip title={tooltipCenter}>
        <circle cx={center} cy={center} r={radius} stroke={strokeColor} strokeWidth={strokeWidth} fill="none" />
      </Tooltip>

      {/* Transparent inner circle for hover */}
      <Tooltip title={tooltipCenter}>
        <circle cx={center} cy={center} r={innerRadius} fill="transparent" />
      </Tooltip>

      {/* Draw bottom layer first (white) */}
      {active > 0 && (
        <Tooltip title={tooltipActive}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={variant === "Light" ? colors.Neutral.Neutral50 : COLORS.active}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${whiteLength} ${circumference - whiteLength}`}
            strokeDashoffset={whiteOffset}
            strokeLinecap="round"
          />
        </Tooltip>
      )}

      {/* Then green over white */}
      {completed > 0 && (
        <Tooltip title={tooltipCompleted}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={COLORS.completed}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${greenLength} ${circumference - greenLength}`}
            strokeDashoffset={greenOffset}
            strokeLinecap="round"
          />
        </Tooltip>
      )}

      {/* Then red on top of all */}
      {overdue > 0 && (
        <Tooltip title={tooltipOverdue}>
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={COLORS.overdue}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${redLength} ${circumference - redLength}`}
            strokeDashoffset={redOffset}
            strokeLinecap="round"
          />
        </Tooltip>
      )}

      {/* Optional center text */}
      <text
        x={center}
        y={center}
        textAnchor="middle"
        dominantBaseline="middle"
        style={{
          fontSize: size * 0.25,
          fill: "#333",
          transform: "rotate(90deg)",
          userSelect: "none"
        }}
      >
        {Math.round(((completed + overdue) / total) * 100)}%
      </text>
    </svg>
  );
};

export default CircularProgressBar;
